<script>
  import { makeLmsRequest } from '../service/LmsClient';
  import { createEventDispatcher, onMount } from 'svelte';
  import { External, M } from '../../external';
  import Loader from '../../Common/Loader.svelte';
  import AssignCirclesPopup from './AssignCirclesPopup.svelte';
  import CommentAdminService from '../../Common/CommentAdminService';
  import RequestAccessPreview from '../popups/RequestAccessPreview.svelte';
  import { showSettingsPopup } from '../service/LmsUIService';

  const dispatch = createEventDispatcher();

  export let circles, accesses, memberId, contactId;

  let sendingRequest = false;

  let unEnroll = (circle) => {
    External.Tools.prompt(
      '',
      External.format(M.MEMBER_UNENROLL_QUESTION, 'x', circle.name),
      async () => {
        sendingRequest = true;
        let result = await makeLmsRequest(`/circle/${circle.id}/enrollment/${contactId}/${memberId}`, 'DELETE');

        sendingRequest = false;
        if (result.ok) {
          dispatch('unenrolled', circle);
        }
      },
      () => {}
    );
  };

  let enroll = async (circle) => {
    sendingRequest = true;
    let result = await makeLmsRequest(`/circle/${circle.id}/enrollment/${contactId}/${memberId}`, 'POST');
    if (result.ok) {
      dispatch('enrolled', circle);
    }
    sendingRequest = false;
  };

  let ban = async (circle) => {
    External.Tools.prompt(
      '',
      External.format(M.MEMBER_BAN_QUESTION, 'x', circle.name),
      async () => {
        sendingRequest = true;
        let result = await makeLmsRequest(`/circle/${circle.id}/ban/${contactId}/${memberId}`, 'POST');
        sendingRequest = false;
        if (result.ok) {
          dispatch('banned', circle);
        }
      },
      () => {}
    );
  };

  let unban = async (circle) => {
    sendingRequest = true;
    let result = await makeLmsRequest(`/circle/${circle.id}/ban/${contactId}/${memberId}`, 'DELETE');
    if (result.ok) {
      dispatch('unbanned', circle);
    }
    sendingRequest = false;
  };

  let approveAccessRequest = async (circle) => {
    sendingRequest = true;
    let result = await makeLmsRequest(`/circle/${circle.id}/access-request/${contactId}/${memberId}`, 'POST');
    if (result.ok) {
      dispatch('approved', circle);
    }
    sendingRequest = false;
  };

  let rejectApproveRequest = async (circle) => {
    sendingRequest = true;
    let result = await makeLmsRequest(`/circle/${circle.id}/access-request/${contactId}/${memberId}`, 'DELETE');
    if (result.ok) {
      dispatch('rejected', circle);
    }
    sendingRequest = false;
  };

  let mute = async (circle) => {
    sendingRequest = true;
    const result = await CommentAdminService.muteMember(memberId, circle.id);
    if (result.ok) {
      dispatch('role_changed', circle);
    }
    sendingRequest = false;
  };

  let unmute = async (circle) => {
    sendingRequest = true;
    const result = await CommentAdminService.unmuteMember(memberId, circle.id);
    if (result.ok) {
      dispatch('role_changed', circle);
    }
    sendingRequest = false;
  };

  let makeAdmin = async (circle) => {
    sendingRequest = true;
    const result = await CommentAdminService.makeAdmin(memberId, circle.id);
    if (result.ok) {
      dispatch('role_changed', circle);
    }
    sendingRequest = false;
  };

  let makeModerator = async (circle) => {
    sendingRequest = true;
    const result = await CommentAdminService.makeModerator(memberId, circle.id);
    if (result.ok) {
      dispatch('role_changed', circle);
    }
    sendingRequest = false;
  };

  let makeOrdinaryMember = async (circle) => {
    sendingRequest = true;
    const result = await CommentAdminService.makeOrdinaryMember(memberId, circle.id);
    if (result.ok) {
      dispatch('role_changed', circle);
    }
    sendingRequest = false;
  };

  let showAssignCirclesPopup = async () => {
    External.Tools.showWindow({
      isModal: false,
      withDarkBackground: true,
      width: 520,
      buttons: [],
      noNav: true,
      noTitle: true,
      forceAtRight: window.showBuilderPopupsOnFullHeight,
      action: {
        onShow: (popup, ui) => {
          popup.bindComponent(
            new AssignCirclesPopup({
              target: popup.getContentArea(),
              props: {
                circles: circles,
                onEnroll: (circle) => {
                  enroll(circle);
                  popup.close();
                }
              }
            })
          );
        }
      }
    });
  };

  const assignAsCommunityManager = (isAdmin, isModerator, onSelect) => {
    const options = [
      { id: 0, name: M.COMMUNITY_ADMIN || 'Admin' },
      { id: 1, name: M.COMMUNITY_MODERATOR || 'Moderator' },
      { id: 2, name: M.MEMBER || 'Member' }
    ];

    const selected = [];
    if (isAdmin) {
      selected.push(options[0]);
    }
    if (isModerator) {
      selected.push(options[1]);
    }
    if (!isAdmin && !isModerator) {
      selected.push(options[2]);
    }

    External.Tools.chooseMultiple({
      single: true,
      allowEmptySelection: true,
      title: M.ASSIGN_COMMUNITY_ROLE,
      message: '',
      noItemsMessage: '',
      items: options,
      selectedItems: selected,
      isModal: true,
      getItemId: function (group) {
        return group.id;
      },
      getItemText: function (group) {
        return group.name;
      },
      onSelect: onSelect
    });
  };

  const reviewAccessRequest = (circle, access) => {
    showSettingsPopup({
      width: 420,
      component: (target, close) =>
        new RequestAccessPreview({
          target: target,
          props: {
            settings: circle.settings.requestAccess,
            previewAnswers: access.extra.accessRequest.responses,
            onApprove: () => {
              approveAccessRequest(circle);
              close();
            },
            onReject: () => {
              rejectApproveRequest(circle)
              close();
            }
          }
        })
    });
  }

  const showCircleActions = (event, circleDetails, roles) => {
    const circleAccess = getCircleAccess(circleDetails.circle.id);
    const items = [];
    if (circleAccess.status === 'BANNED') {
      items.push({ text: M.REMOVE_BAN, icon: 'fa-user-plus', tag: 'unban' });
    } else if (circleAccess.status === 'ACCESS_REQUESTED') {
      items.push({ text: 'Review Access Request', icon: 'fa-minus', tag: 'review-access-request' });
    } else {
      items.push({ text: M.UNENROLL, icon: 'fa-minus', tag: 'unenroll' });
      items.push({ text: M.BAN, icon: 'fa-user-slash', tag: 'ban' });
      if (roles && isMuted(roles, circleDetails.circle.id)) {
        items.push({ text: M.UNMUTE, icon: 'fa-comment', tag: 'unmute' });
      } else {
        items.push({ text: M.MUTE, icon: 'fa-comment-slash', tag: 'mute' });
        items.push({ text: M.ASSIGN_COMMUNITY_ROLE, icon: 'fa-user', tag: 'assign-role' });
      }
    }

    External.Tools.showTooltipOptions(External.$(event.target), items, function (target, idx, text, tag) {
      switch (tag) {
        case 'unenroll':
          unEnroll(circleDetails.circle);
          break;
        case 'ban':
          ban(circleDetails.circle);
          break;
        case 'unban':
          unban(circleDetails.circle);
          break;
        case 'mute':
          mute(circleDetails.circle);
          break;
        case 'unmute':
          unmute(circleDetails.circle);
          break;
        case 'assign-role':
          assignAsCommunityManager(
            isAdmin(roles, circleDetails.circle.id),
            isModerator(roles, circleDetails.circle.id),
            function (selected) {
              if (selected.length > 0) {
                if (selected[0].id === 0) {
                  makeAdmin(circleDetails.circle);
                } else if (selected[0].id === 1) {
                  makeModerator(circleDetails.circle);
                } else {
                  makeOrdinaryMember(circleDetails.circle);
                }
              }
            }
          );
          break;
        case 'review-access-request':
          reviewAccessRequest(circleDetails.circle, getCircleAccess(circleDetails.circle.id));
          break;
      }
    });
  };

  const getCircleAccess = (circleId) => {
    return accesses.find((iter) => {
      return iter.itemId === circleId && iter.itemType === 'CIRCLE';
    });
  };

  const isAccessRequested = (circleId) => {
    const circleAccess = getCircleAccess(circleId);
    return circleAccess.status === 'ACCESS_REQUESTED';
  };

  const isBanned = (circleId) => {
    const circleAccess = getCircleAccess(circleId);
    return circleAccess.status === 'BANNED';
  };

  const isMuted = (roles, circleId) => {
    if (!roles) {
      return false;
    }

    const roleIndex = roles[circleId];
    return roleIndex >= 1_000_000; // 0,1,2 is defined roles. service defines max int32 as anonymous.
  };

  const isAdmin = (roles, circleId) => {
    if (!roles) {
      return false;
    }

    const roleIndex = roles[circleId];
    return roleIndex === 0;
  };

  const isModerator = (roles, circleId) => {
    if (!roles) {
      return false;
    }

    const roleIndex = roles[circleId];
    return roleIndex === 1;
  };

  let communityRoles = null;
  onMount(async () => {
    const result = await CommentAdminService.getMemberDetails(memberId);
    communityRoles = result.roles;
  });
</script>

<div class="col20 f-left">
  <div class="col20 f-left mb-15 flex align-center">
    <h4 class="roboto title-line">{M.CIRCLES}</h4>
    <a class="small-button blue with-icon rounded-effect" on:click={showAssignCirclesPopup}>{M.ASSIGN_CIRCLE}</a>
  </div>
  <div class="col20 f-left">
    {#each circles as circleDetails}
      <div
        class="filter-option flex align-center circle-profile-tag"
        class:banned={isBanned(circleDetails.circle.id)}
        class:muted={communityRoles && isMuted(communityRoles, circleDetails.circle.id)}
        class:access-requested={isAccessRequested(circleDetails.circle.id)}
        style="background: rgb(236, 237, 243) none repeat scroll 0% 0%;">
        <span>{circleDetails.circle.name}</span>
        <a
          class="filter-delete circle-profile-options"
          on:click={(event) => showCircleActions(event, circleDetails, communityRoles)}
          class:disable-anchor={sendingRequest === true}>
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#222222"><path d="M240-400q-33 0-56.5-23.5T160-480q0-33 23.5-56.5T240-560q33 0 56.5 23.5T320-480q0 33-23.5 56.5T240-400Zm240 0q-33 0-56.5-23.5T400-480q0-33 23.5-56.5T480-560q33 0 56.5 23.5T560-480q0 33-23.5 56.5T480-400Zm240 0q-33 0-56.5-23.5T640-480q0-33 23.5-56.5T720-560q33 0 56.5 23.5T800-480q0 33-23.5 56.5T720-400Z"/></svg>
        </a>
      </div>
    {/each}
  </div>
  {#if sendingRequest === true}
    <Loader compact={true} />
  {/if}
</div>

<style>
  .disable-anchor {
    pointer-events: none;
  }
</style>
